import { Box, Grid, Stack, Typography } from '@mui/material';
import Point from '../../../../assets/svg/Point';
import SquareExclamatioMark from '../../../../assets/svg/SquareExclamatioMark';
import { getIncidentAddress } from '../../../../pages/Incidents/utils/incidentUtils';
import Clock from '../../../../assets/svg/Clock';
import moment from 'moment';
import Graphics from '../../../../assets/svg/Graphics';
import { dateTimeFormat } from '../../../../utils/formatDate';
import { capitalizeFirstLetter } from '../../../../utils/capitalizeFirstLetter';
import { useMemo } from 'react';
import { statusMatching } from '../../../../pages/Incidents/utils/statusMatching';

const BaseInfo = ({ selectedIncidentInfo }) => {
  const nerisStatus = useMemo(
    () => statusMatching(selectedIncidentInfo?.neris_status),
    [selectedIncidentInfo]
  );

  return (
    <>
      <Box
        sx={{
          borderBottom: '1px solid #E1E1E1',
          pl: 3,
          pr: 3,
          pb: 2,
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          useFlexGap
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ width: '50%' }}>
            <Box className="d-flex align-items-center">
              <Point />
              <Typography className="subTitle ms-1">Address</Typography>
            </Box>
            <Typography className="text">
              {getIncidentAddress(selectedIncidentInfo.base.location)}
            </Typography>
          </Box>
          <Box sx={{ width: '50%' }}>
            <Box className="d-flex align-items-center">
              <SquareExclamatioMark />
              <Typography className="subTitle ms-1">Type</Typography>
            </Box>
            {selectedIncidentInfo.incident_types
              ? selectedIncidentInfo.incident_types.map((item, index, arr) => (
                  <Typography className="text mt-1" key={item.type + index}>
                    {item.type}
                    {arr.length - 1 > index ? ', ' : ''}
                  </Typography>
                ))
              : '-'}
          </Box>
        </Stack>
      </Box>

      <Box
        sx={{
          borderBottom: '1px solid #E1E1E1',
          pl: 3,
          pr: 3,
          pb: 2,
          mt: 2,
        }}
      >
        <Typography className="sectionTitle">Call Information</Typography>
        <Stack
          direction="row"
          spacing={2}
          useFlexGap
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ width: '33.3%' }}>
            <Box className="d-flex align-items-center">
              <Clock />
              <Typography className="subTitle ms-1">Create</Typography>
            </Box>
            <Typography className="text">
              {dateTimeFormat(selectedIncidentInfo.dispatch.call_create)}
            </Typography>
          </Box>
          <Box sx={{ width: '33.3%' }}>
            <Box className="d-flex align-items-center">
              <Clock />
              <Typography className="subTitle ms-1">Answered</Typography>
            </Box>
            <Typography className="text">
              {dateTimeFormat(selectedIncidentInfo.dispatch.call_answered)}
            </Typography>
          </Box>
          <Box sx={{ width: '33.3%' }}>
            <Box className="d-flex align-items-center">
              <Clock />
              <Typography className="subTitle ms-1">Arrival</Typography>
            </Box>
            <Typography className="text">
              {dateTimeFormat(selectedIncidentInfo.dispatch.call_arrival)}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Box
        sx={{
          pl: 3,
          pr: 3,
          pb: 2,
          mt: 2,
        }}
      >
        <Typography className="sectionTitle">Status Information</Typography>
        <Stack
          direction="row"
          spacing={2}
          useFlexGap
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ width: '50%' }}>
            <Box className="d-flex align-items-center">
              <Graphics />
              <Typography className="subTitle ms-1">Status</Typography>
            </Box>
            <Box
              sx={{
                background:
                  selectedIncidentInfo.status?.toLowerCase() === 'closed'
                    ? '#F3F3F3'
                    : '#FFF89A',
                padding: '5px 15px',
                borderRadius: '3px',
                width: 'fit-content',
                mt: '10px',
              }}
            >
              {capitalizeFirstLetter(selectedIncidentInfo.status)}
            </Box>
          </Box>
          <Box sx={{ width: '50%' }}>
            <Box className="d-flex align-items-center">
              <Graphics />
              <Typography className="subTitle ms-1">NERIS Status</Typography>
            </Box>
            <Box
              sx={{
                background: nerisStatus.bg,
                color: nerisStatus.color,
                padding: '5px 15px',
                borderRadius: '999px',
                width: 'fit-content',
                mt: '10px',
              }}
            >
              {nerisStatus.text}
            </Box>
          </Box>
        </Stack>
      </Box>

      <Box
        sx={{
          pl: 3,
          pr: 3,
          pb: 2,
          mt: 3,
        }}
      >
        <Typography className="sectionTitle mb-0">Unit Responses</Typography>

        <Grid sx={{ flexGrow: 1, mt: '0' }} container spacing={2}>
          {selectedIncidentInfo.unit_responses?.map((unit, index) => (
            <Grid item xs={4} key={unit.reported_unit_id + index}>
              <Box key={index} className="unit-box">
                <Typography className="unit-title">
                  Unit ID: {unit.reported_unit_id}
                </Typography>

                {unit.dispatch ? (
                  <>
                    <Typography className="unit-text">
                      Dispatch: {moment(unit.dispatch).format('DD.MM.YYYY')}
                      {', '}
                      <span>{moment(unit.dispatch).format('HH:mm:ss')}</span>
                    </Typography>
                  </>
                ) : (
                  <Typography className="unit-text">Dispatch: -</Typography>
                )}

                {unit.on_scene ? (
                  <>
                    <Typography className="unit-text">
                      On Scene: {moment(unit.on_scene).format('DD.MM.YYYY')}
                      {', '}
                      <span>{moment(unit.on_scene).format('HH:mm:ss')}</span>
                    </Typography>
                  </>
                ) : (
                  <Typography className="unit-text">On Scene: -</Typography>
                )}

                {unit.unit_clear ? (
                  <>
                    <Typography className="unit-text">
                      Clear: {moment(unit.unit_clear).format('DD.MM.YYYY')}
                      {', '}
                      <span>{moment(unit.unit_clear).format('HH:mm:ss')}</span>
                    </Typography>
                  </>
                ) : (
                  <Typography className="unit-text">Clear: -</Typography>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </>
  );
};

export default BaseInfo;
